import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquare, X } from 'lucide-react';
import { TaskComment, TaskCommentFieldAnchor, TaskTypeInstructions } from '../../types';
import { TaskMode, type TaskFacts, TaskTypeModule } from '../../taskTypes/module';
import { createTaskTypeHost } from '../../taskTypes/host';
import { getTaskTypeInstructionsHref } from '../../taskTypes/instructions';
import { CommentColumn } from './CommentColumn';
import { ConfirmDialog } from './ConfirmDialog';
import { FieldCommentDialog } from './FieldCommentDialog';
import { TaskExampleModal } from './TaskExampleModal';
import { VerificationColumn } from './VerificationColumn';
import { useAppUpdate } from '../../contexts/AppUpdateContext';
import {
  ReviewProvider,
  ReviewContextValue,
  TaskTabRegistration,
} from './ReviewContext';
import { canRequestChanges, canResubmit, type ResubmitBlocker } from './reviewGates';
import { TaskShellHeader } from './TaskShellHeader';
import { TaskShellFooter } from './TaskShellFooter';
import { TaskShellProps } from './taskShellTypes';
import { useTaskPanelState } from './commentPanelState';
import {
  TASK_SHELL_PANELS,
  availablePanels,
  resolveActivePanel,
  type TaskShellPanelContext,
} from './panels/registry';
import { taskCommentGroupingKey } from '../../utils/taskCommentAnchors';

export type { TaskShellActions, TaskShellProps } from './taskShellTypes';

const ACTIVE_HIGHLIGHT_MS = 2500;

export function TaskShell({
  mode,
  module,
  task,
  result,
  onResultChange,
  canEditResult = false,
  onBeforeReload,
  busy,
  pendingAction,
  pendingCommentId,
  error,
  actions,
  canClaimReview,
  comments = [],
  isAdminViewer = false,
  currentUserUid = null,
  host,
  canRename = false,
  fullBleed = false,
  fullBleedHeader = false,
  onVerificationPanelVisibilityChange,
  embedded = false,
  stickyTopPx = 64,
  instructions = null,
  validatorEnabled = false,
  validatorCommentsByField,
  dismissedValidatorIds,
  validatorStatus = 'idle',
  validatorError = null,
  openPanelRequest,
}: TaskShellProps) {
  const { registerBeforeReload } = useAppUpdate();
  const taskTypeHost = useMemo(
    () => host ?? createTaskTypeHost(currentUserUid),
    [currentUserUid, host],
  );
  // Persisted Firestore values are untrusted at this boundary. Runtime
  // parsers normalise legacy/malformed shapes before any task view or submit
  // validator can read them. The parser contract is intentionally pure so it
  // is also usable by previews and isolated task-type tests.
  const parsedPayload = useMemo(
    () => module.parsePayload(task?.payload ?? module.samplePayload),
    [module, task?.payload],
  );
  const parsedResult = useMemo(
    () => (result == null ? null : module.parseResult(result)),
    [module, result],
  );
  const structuredValidationIssues = useMemo(
    () => module.validateSubmission(parsedResult),
    [module, parsedResult],
  );
  // A task type can route Submit through a modal that collects something the
  // result is missing (see `submitConfirmation` in taskTypes/module.ts). The
  // issues that modal collects must not disable the Submit button, or the
  // modal would be unreachable — they're still enforced, by the modal's own
  // gate and by the submit action's `isSubmittable` check.
  const submitConfirmation = mode === 'work' ? module.submitConfirmation ?? null : null;
  const blockingValidationIssues = useMemo(
    () => (submitConfirmation
      ? structuredValidationIssues.filter((issue) => !submitConfirmation.resolvesIssue(issue))
      : structuredValidationIssues),
    [structuredValidationIssues, submitConfirmation],
  );
  const needsSubmitConfirmation = !!submitConfirmation
    && submitConfirmation.isRequired(parsedResult);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [commentComposer, setCommentComposer] = useState<{
    fieldPath: string;
    fieldAnchor?: TaskCommentFieldAnchor;
  } | null>(null);
  const [activeFieldPath, setActiveFieldPath] = useState<string | null>(null);
  const [focusedFieldPath, setFocusedFieldPathState] = useState<string | null>(null);
  const [focusedFieldAnchor, setFocusedFieldAnchor] = useState<TaskCommentFieldAnchor | null>(null);
  // Labels published by FieldCommentSlots, keyed by field path. The footer
  // uses them to name the fields whose reviewer threads still block
  // resubmission. Entries are kept once seen so a field on a tab that is
  // currently unmounted keeps its proper name instead of flickering back
  // to a humanized path; the latest registration for a path wins.
  const [fieldLabels, setFieldLabels] = useState<ReadonlyMap<string, string>>(() => new Map());
  const registerFieldLabel = useCallback((fieldPath: string, label: string) => {
    setFieldLabels((prev) => {
      if (prev.get(fieldPath) === label) return prev;
      const next = new Map(prev);
      next.set(fieldPath, label);
      return next;
    });
  }, []);
  const [abandonOpen, setAbandonOpen] = useState(false);
  const [releaseReviewOpen, setReleaseReviewOpen] = useState(false);
  const [taskExampleOpen, setTaskExampleOpen] = useState(false);
  // Comment panel is an on-demand slide-over from the right, NOT a persistent
  // right rail. Open by default on every task so comments are immediately
  // visible; the user's per-task toggle choice is remembered in sessionStorage
  // (session scope) so closing it sticks while navigating inside the session.
  const {
    open: commentPanelOpen,
    setOpen: setPersistedCommentPanelOpen,
    panelId: preferredPanelId,
    setPanelId: setPreferredPanelId,
  } = useTaskPanelState(task?.id);
  const taskTabRegistrationsRef = useRef<TaskTabRegistration[]>([]);

  const meta = task
    ? {
        title: task.title,
        description: task.description,
        reviewRound: task.reviewRound,
      }
    : module.sampleTaskMeta;
  const instructionsTaskTypeId = task?.taskType ?? module.id;
  const instructionsHref = instructions
    ? getTaskTypeInstructionsHref(instructionsTaskTypeId, instructions)
    : null;
  const showStatusChip = mode !== 'work';

  // Reviewer moderation is only meaningful while the reviewer is actually
  // looking at this task in review mode. We don't expose moderation in
  // 'work' (annotator) or 'preview' (admin Task Types tool). The server-side
  // rules are the real gate — this just hides buttons that would 403.
  const canModerateLlm = mode === 'review' && !!(
    actions?.onApproveLlmComment
    || actions?.onEditLlmComment
    || actions?.onDeleteLlmComment
  );

  // Who's allowed to flip a root comment from unresolved to resolved (and
  // back). The owner during rework is the original case; reviewers in review
  // mode and admins on any real task can also do it when the writer's reply
  // satisfies the comment but they didn't mark it themselves. Preview mode
  // (admin Task Types tool) stays read-only — there's no real task to act on.
  const canResolveComments =
    mode === 'work'
    || canModerateLlm
    || (isAdminViewer && mode !== 'preview');

  const canViewFieldDiffs = mode === 'review' || isAdminViewer;

  // Pending LLM comments are raw AI output the human reviewer hasn't blessed
  // yet. Only reviewers (who need to moderate them) and admins (who audit)
  // should see them in the UI. Annotators in 'work' mode see the same set
  // the email sends: human comments plus APPROVED LLM comments.
  const fieldComments = useMemo(() => {
    const allField = comments.filter((c) => c.kind === 'field' && typeof c.fieldPath === 'string');
    if (canModerateLlm || isAdminViewer) return allField;
    return allField.filter((c) => c.source !== 'llm' || c.llmStatus === 'approved');
  }, [comments, canModerateLlm, isAdminViewer]);

  // Roots only for the pin counts and field-level interactions — replies
  // are conversation, not findings.
  const commentsByField = useMemo(() => {
    const m = new Map<string, TaskComment[]>();
    for (const c of fieldComments) {
      if (c.parentCommentId != null) continue;
      const groupingKey = taskCommentGroupingKey(c);
      if (!groupingKey) continue;
      const arr = m.get(groupingKey);
      if (arr) arr.push(c);
      else m.set(groupingKey, [c]);
    }
    return m;
  }, [fieldComments]);

  // Replies keyed by root id — rendered under each root in the focused view.
  const commentsByRoot = useMemo(() => {
    const m = new Map<string, TaskComment[]>();
    for (const c of fieldComments) {
      if (c.parentCommentId == null) continue;
      const arr = m.get(c.parentCommentId);
      if (arr) arr.push(c);
      else m.set(c.parentCommentId, [c]);
    }
    m.forEach((list) => {
      list.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    });
    return m;
  }, [fieldComments]);

  // Show the column whenever there's anything to render AND we're in a mode
  // Clear the active highlight after a short window so it doesn't stick.
  useEffect(() => {
    if (!activeFieldPath) return;
    const id = setTimeout(() => setActiveFieldPath(null), ACTIVE_HIGHLIGHT_MS);
    return () => clearTimeout(id);
  }, [activeFieldPath]);

  const setCommentPanelOpen = useCallback((next: boolean) => {
    // Closing the panel should drop any field highlights tied to it — the
    // focused-field ring and the active-field transient pulse both exist
    // to anchor the user to a comment that's currently visible. With the
    // panel gone, those rings become orphan visual noise on the form.
    if (!next) {
      setFocusedFieldPathState(null);
      setFocusedFieldAnchor(null);
      setActiveFieldPath(null);
    }
    setPersistedCommentPanelOpen(next);
  }, [setPersistedCommentPanelOpen]);

  const registerTaskTab = useCallback((registration: TaskTabRegistration) => {
    taskTabRegistrationsRef.current.push(registration);
    return () => {
      const index = taskTabRegistrationsRef.current.indexOf(registration);
      if (index >= 0) taskTabRegistrationsRef.current.splice(index, 1);
    };
  }, []);

  const navigateToField = useCallback((fieldPath: string) => {
    taskTabRegistrationsRef.current.forEach((registration) => {
      const ownsField = registration.fieldPathPrefixes.some((prefix) => (
        fieldPath === prefix || fieldPath.startsWith(`${prefix}.`)
      ));
      if (ownsField) registration.select();
    });
    setActiveFieldPath(fieldPath);
  }, []);

  // Focusing a field from anywhere (pin click, column thread click) must
  // bring the panel onto the screen — otherwise the user sees the field ring
  // highlight but has no visible thread to interact with.
  const resolveCommentFieldPath = useCallback((comment: TaskComment): string | null => {
    if (comment.fieldAnchorState === 'orphaned') return null;
    if (comment.fieldAnchor && module.resolveCommentFieldPath) {
      return module.resolveCommentFieldPath(comment.fieldAnchor, parsedResult);
    }
    return comment.fieldPath;
  }, [module, parsedResult]);

  // Keep the focused view on the same rubric item while the writer edits the
  // array. Its visible numeric path may change on every insert/delete, but
  // the focused anchor remains stable until that requirement is deleted.
  const effectiveFocusedFieldPath = useMemo(() => {
    if (!focusedFieldAnchor || !module.resolveCommentFieldPath) return focusedFieldPath;
    return module.resolveCommentFieldPath(focusedFieldAnchor, parsedResult);
  }, [focusedFieldAnchor, focusedFieldPath, module, parsedResult]);

  const setFocusedFieldPath = useCallback((
    path: string | null,
    fieldAnchor?: TaskCommentFieldAnchor,
  ) => {
    if (path != null) {
      setPreferredPanelId('comments');
      navigateToField(path);
    }
    setFocusedFieldAnchor(path == null ? null : fieldAnchor ?? null);
    setFocusedFieldPathState(path);
    if (path != null) setCommentPanelOpen(true);
  }, [navigateToField, setCommentPanelOpen, setPreferredPanelId]);

  // The footer's resubmit notice links each blocking field here. Field-level
  // threads jump straight to the field's focused view; a task-level thread
  // has no field to anchor on, so just make sure the comments panel is open.
  const focusBlockedField = useCallback((
    fieldPath: string | null,
    fieldAnchor?: TaskCommentFieldAnchor,
  ) => {
    if (fieldPath != null) {
      setFocusedFieldPath(fieldPath, fieldAnchor);
      return;
    }
    setPreferredPanelId('comments');
    setCommentPanelOpen(true);
  }, [setCommentPanelOpen, setFocusedFieldPath, setPreferredPanelId]);

  // Esc closes the panel. Binds while open.
  useEffect(() => {
    if (!commentPanelOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCommentPanelOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentPanelOpen]);

  // Measured top offset for the comment rail. The task header's height
  // depends on content width (title wraps on narrow screens, badges/toggles
  // can wrap too), so ResizeObserver keeps this accurate through resizes.
  // `stickyTopPx` accounts for the app header plus any page-level sticky bar.
  const taskHeaderRef = useRef<HTMLDivElement>(null);
  const [taskHeaderHeight, setTaskHeaderHeight] = useState<number>(0);
  useEffect(() => {
    const el = taskHeaderRef.current;
    if (!el) return;
    setTaskHeaderHeight(el.offsetHeight);
    if (typeof ResizeObserver === 'undefined') return;
    // Writing height straight from the ResizeObserver callback re-lays out the
    // observed element during delivery, which is what makes Chrome fire
    // "ResizeObserver loop completed with undelivered notifications". Defer
    // the write to the next animation frame so the state update lands outside
    // delivery. Same pattern used in AutoGrowTextarea.
    let disposed = false;
    let frame = 0;
    let nextHeight = el.offsetHeight;
    const flush = () => {
      frame = 0;
      if (disposed) return;
      setTaskHeaderHeight(nextHeight);
    };
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        nextHeight = entry.contentRect.height;
      }
      if (frame) return;
      frame = requestAnimationFrame(flush);
    });
    ro.observe(el);
    return () => {
      disposed = true;
      if (frame) cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, []);
  // Panel top should line up with the TOP of the main body section, not the
  // bottom of the sticky task header. Body uses `py-6` (24px top padding) on
  // the wrapper, so we mirror that offset here. Same 32px on the right edge
  // as `lg:px-8` on the body so the panel visually matches the main card.
  const BODY_TOP_PADDING = 24;
  const railTopPx = stickyTopPx + taskHeaderHeight + BODY_TOP_PADDING;

  // Reviewer gate: require at least one unresolved thread before the task
  // can be bounced back. If everything is resolved, there's nothing to
  // request changes against — disable the button and surface a hint.
  const canRequestChangesNow = canRequestChanges(fieldComments);

  // `fieldPath` on an anchored comment is only a cached path from the time
  // the comment was written. Resolve it before the writer gate builds its
  // jump links, otherwise a moved requirement would still be described as
  // blocking at its old array position.
  const commentsForResubmitGate = useMemo(
    () => fieldComments.map((comment) => {
      if (!comment.fieldAnchor && comment.fieldAnchorState !== 'orphaned') return comment;
      const fieldPath = resolveCommentFieldPath(comment);
      return {
        ...comment,
        fieldPath,
        ...(fieldPath == null && parsedResult != null ? { fieldAnchorState: 'orphaned' as const } : {}),
      };
    }),
    [fieldComments, parsedResult, resolveCommentFieldPath],
  );

  // Writer gate: every unresolved root must be resolved OR replied-to by the
  // writer before they can submit. Not scoped to review round — if a comment
  // is still unresolved, it's still legitimate feedback regardless of when it
  // was written. Initial submissions (no comments yet) pass trivially.
  const submitGate = useMemo(() => {
    if (!task) return { ok: true, blockedBy: [] as ResubmitBlocker[] };
    if (mode !== 'work') return { ok: true, blockedBy: [] as ResubmitBlocker[] };
    return canResubmit(commentsForResubmitGate, task.ownerId);
  }, [task, mode, commentsForResubmitGate]);

  // Set of root comment ids that are blocking the writer's resubmit. Passed
  // into context so each Thread can render a per-thread "resolve or reply"
  // warning at the point of action — more findable than a footer-only hint.
  const blockedRootIds = useMemo(
    () => new Set(submitGate.blockedBy.map((b) => b.commentId)),
    [submitGate],
  );

  // Visible validator-comment count (after dismissals) drives the badge
  // on the Validate button. Total entries are flattened across fields,
  // then filtered by the dismissed set. When the validator hasn't run,
  // the map is empty and this is 0 — the button shows "Validate".
  const validatorVisibleCount = useMemo(() => {
    if (!validatorCommentsByField) return 0;
    let total = 0;
    // .forEach to avoid the for-of-on-Map downlevelIteration requirement —
    // CRA's tsconfig targets es5 and doesn't enable downlevelIteration.
    validatorCommentsByField.forEach((list, fieldPath) => {
      for (let i = 0; i < list.length; i++) {
        const key = `${fieldPath}::${i}`;
        if (!dismissedValidatorIds || !dismissedValidatorIds.has(key)) total += 1;
      }
    });
    return total;
  }, [validatorCommentsByField, dismissedValidatorIds]);

  const handleRunValidation = useCallback(async () => {
    if (!actions?.onRunValidation) return false;
    // Validation can take several minutes. Open the rail as soon as the
    // action starts so the user gets immediate feedback, including failures.
    setPreferredPanelId('verification');
    setCommentPanelOpen(true);
    const succeeded = await actions.onRunValidation();
    return succeeded;
  }, [actions, setCommentPanelOpen, setPreferredPanelId]);

  const ctxValue: ReviewContextValue = useMemo(
    () => ({
      mode,
      comments: fieldComments,
      commentsByField,
      commentsByRoot,
      activeFieldPath,
      setActiveFieldPath,
      registerTaskTab,
      navigateToField,
      registerFieldLabel,
      focusedFieldPath: effectiveFocusedFieldPath,
      setFocusedFieldPath,
      addReply: async (rootCommentId, text) => {
        if (actions?.onAddReply) await actions.onAddReply(rootCommentId, text);
      },
      editComment: async (commentId, text) => {
        if (actions?.onEditComment) await actions.onEditComment(commentId, text);
      },
      deleteComment: async (commentId) => {
        if (actions?.onDeleteComment) await actions.onDeleteComment(commentId);
      },
      currentRound: task?.reviewRound ?? 0,
      ownerId: task?.ownerId ?? null,
      currentUserUid,
      currentResult: parsedResult,
      canViewFieldDiffs,
      openCommentComposer: (fieldPath, fieldAnchor) => setCommentComposer({ fieldPath, fieldAnchor }),
      focusedFieldAnchor,
      resolveCommentFieldPath,
      markAddressed: async (commentId, addressed) => {
        if (actions?.onMarkAddressed) await actions.onMarkAddressed(commentId, addressed);
      },
      canResolveComments,
      pendingCommentId: pendingCommentId ?? null,
      isAdminViewer,
      canModerateLlm,
      approveLlmComment: async (commentId) => {
        if (actions?.onApproveLlmComment) await actions.onApproveLlmComment(commentId);
      },
      editLlmComment: async (commentId, text) => {
        if (actions?.onEditLlmComment) await actions.onEditLlmComment(commentId, text);
      },
      deleteLlmComment: async (commentId) => {
        if (actions?.onDeleteLlmComment) await actions.onDeleteLlmComment(commentId);
      },
      blockedRootIds,
      validatorCommentsByField: validatorCommentsByField ?? new Map(),
      dismissedValidatorIds: dismissedValidatorIds ?? new Set(),
      dismissValidatorComment: (fieldPath, originalIndex) => {
        if (actions?.onDismissValidatorComment) {
          actions.onDismissValidatorComment(fieldPath, originalIndex);
        }
      },
      restoreValidatorComment: (fieldPath, originalIndex) => {
        if (actions?.onRestoreValidatorComment) {
          actions.onRestoreValidatorComment(fieldPath, originalIndex);
        }
      },
    }),
    [
      mode,
      fieldComments,
      commentsByField,
      commentsByRoot,
      activeFieldPath,
      registerTaskTab,
      navigateToField,
      registerFieldLabel,
      effectiveFocusedFieldPath,
      focusedFieldAnchor,
      setFocusedFieldPath,
      actions,
      pendingCommentId,
      isAdminViewer,
      canModerateLlm,
      canResolveComments,
      task,
      currentUserUid,
      parsedResult,
      canViewFieldDiffs,
      resolveCommentFieldPath,
      blockedRootIds,
      validatorCommentsByField,
      dismissedValidatorIds,
    ],
  );

  // Task types still treat `mode !== 'work'` as read-only. Override only the
  // body-mode here so admins can correct submitted answers during review
  // without turning the whole shell into writer mode.
  const bodyMode: TaskMode = mode === 'work' || canEditResult ? 'work' : mode;
  const onChangeBody = bodyMode === 'work' ? onResultChange : () => {};

  // Submit opens the confirmation modal instead of submitting when the task
  // type asks for it. Only the footer gets the swapped action — the header's
  // actions are unrelated.
  const footerActions = useMemo(() => {
    if (!actions) return actions;
    const next = actions.onRunValidation
      ? { ...actions, onRunValidation: handleRunValidation }
      : actions;
    if (!next.onSubmit || !needsSubmitConfirmation) return next;
    return {
      ...next,
      onSubmit: async () => { setSubmitConfirmOpen(true); },
    };
  }, [actions, handleRunValidation, needsSubmitConfirmation]);

  // Drop the modal if the condition that opened it goes away (the draft
  // changed, the task left work mode, or the submit landed).
  useEffect(() => {
    if (!needsSubmitConfirmation) setSubmitConfirmOpen(false);
  }, [needsSubmitConfirmation]);

  // Full-bleed when the caller asked for it (BrowseTasksPage wraps the shell
  // in its own padded main) OR when the task type module opts into wide layout
  // (e.g. browsecomp's two-panel helper+form split needs real width).
  const bleed = fullBleed || module.widthMode === 'wide';
  const headerBleed = bleed || fullBleedHeader;
  const WorkFooter = mode === 'work' ? module.workFooter : undefined;

  // Unresolved root threads — drives the badge on the Comments toggle. Filters
  // to roots only so conversation replies don't inflate the "things to act on"
  // count.
  const unresolvedRootCount = useMemo(
    () => fieldComments.filter((c) => c.parentCommentId == null && !c.addressed).length,
    [fieldComments],
  );

  // The side rail is a registry, not a union: which panels exist, whether
  // each is available, and what its badge says all come from
  // `panels/registry`. Adding one is a descriptor plus a component.
  const panelContext: TaskShellPanelContext = useMemo(() => ({
    mode,
    isAdminViewer,
    fieldComments,
    unresolvedRootCount,
    currentRound: task?.reviewRound ?? 0,
    validatorEnabled,
    canRunValidation: !!actions?.onRunValidation,
    validatorStatus,
    validatorError,
    validatorVisibleCount,
    submissionValidation: mode === 'work'
      ? blockingValidationIssues[0]?.message ?? null
      : null,
  }), [
    mode, isAdminViewer, fieldComments, unresolvedRootCount, task?.reviewRound,
    validatorEnabled, actions?.onRunValidation, validatorStatus, validatorError,
    validatorVisibleCount, blockingValidationIssues,
  ]);

  // One object for every per-task fact a view may read. Adding a fact is a
  // field here and a field on `TaskFacts`, not a prop threaded through four
  // layers and 16 views.
  const taskFacts: TaskFacts = useMemo(() => ({
    taskId: task?.id ?? null,
    campaignId: task?.campaignId ?? null,
    taskType: task?.taskType ?? null,
    domainId: task?.domainId ?? null,
    domainLabel: task?.domainLabel ?? null,
    status: task?.status ?? null,
    expertId: task?.expertId ?? null,
    submittedAt: task?.submittedAt ?? null,
    resultLoadError: task?.resultLoadError ?? null,
  }), [
    task?.id, task?.campaignId, task?.taskType, task?.domainId,
    task?.domainLabel, task?.status, task?.expertId, task?.submittedAt,
    task?.resultLoadError,
  ]);

  const panels = useMemo(() => availablePanels(panelContext), [panelContext]);
  const activePanel = resolveActivePanel(preferredPanelId, panels);
  const railAvailable = panels.length > 0;

  const verificationPanelVisible = commentPanelOpen && activePanel?.id === 'verification';

  useEffect(() => {
    onVerificationPanelVisibilityChange?.(verificationPanelVisible);
  }, [onVerificationPanelVisibilityChange, verificationPanelVisible]);

  // Pull the rail open on the panel the page asks for. Skips the initial 0 so
  // mounting doesn't force it open. Generalised from the verification-only
  // token: any panel can be requested by id.
  useEffect(() => {
    if (!openPanelRequest?.token) return;
    setPreferredPanelId(openPanelRequest.panelId);
    setCommentPanelOpen(true);
  }, [openPanelRequest?.token, openPanelRequest?.panelId, setPreferredPanelId, setCommentPanelOpen]);

  useEffect(() => () => {
    onVerificationPanelVisibilityChange?.(false);
  }, [onVerificationPanelVisibilityChange]);

  const togglePanel = useCallback((panelId: string) => {
    if (commentPanelOpen && activePanel?.id === panelId) {
      setCommentPanelOpen(false);
      return;
    }
    setPreferredPanelId(panelId);
    setCommentPanelOpen(true);
  }, [commentPanelOpen, activePanel?.id, setCommentPanelOpen, setPreferredPanelId]);

  useEffect(() => {
    if (!canEditResult || !onBeforeReload) return undefined;
    return registerBeforeReload(onBeforeReload);
  }, [canEditResult, onBeforeReload, registerBeforeReload]);

  return (
    <ReviewProvider value={ctxValue}>
      {/* Layout shell. In embedded mode (Browse Tasks), wrap the
          [header + body] left column and the comment aside in a flex row so
          the panel sits to the right on lg+ screens (matches the review/
          rework layout). On smaller viewports the row collapses to a column
          and the panel falls back to inline-below-body. In non-embedded mode
          we keep the original three-sibling structure (sticky header,
          fixed-positioned body, fixed-positioned aside) — `display: contents`
          on the wrappers makes them disappear from layout so positioning
          continues to resolve against the page root. */}
      <div className={embedded ? 'flex flex-col gap-4 lg:flex-row lg:items-start' : 'contents'}>
        <div className={embedded ? 'min-w-0 flex-1' : 'contents'}>
      <TaskShellHeader
        mode={mode}
        task={task}
        title={meta.title}
        description={meta.description}
        taskHeaderRef={taskHeaderRef}
        bleed={headerBleed}
        embedded={embedded}
        stickyTopPx={stickyTopPx}
        instructionsHref={instructionsHref}
        canRename={canRename}
        actions={actions}
        isAdminViewer={isAdminViewer}
        canClaimReview={canClaimReview}
        showStatusChip={showStatusChip}
        panelToggles={panels.map((panel) => ({
          id: panel.id,
          label: panel.label,
          badge: panel.badgeCount(panelContext),
          open: commentPanelOpen && activePanel?.id === panel.id,
          onToggle: () => togglePanel(panel.id),
        }))}
        busy={busy}
        pendingAction={pendingAction}
        onOpenTaskExample={() => setTaskExampleOpen(true)}
        onOpenAbandon={() => setAbandonOpen(true)}
        onOpenReleaseReview={() => setReleaseReviewOpen(true)}
      />

      {/* Body card — fixed to the viewport below the sticky headers. The card
          itself doesn't scroll; its inner `<div>` does. The footer is pinned
          inside the card so Submit / Abandon / etc. stay reachable without
          scrolling to the bottom of a long form. Width matches the scrolling
          layout the rest of the page used before (`max-w-6xl` narrow mode,
          full-bleed wide mode); right edge shrinks when the comment rail is
          open so the two cards don't overlap. */}
      <div
        style={embedded ? undefined : { top: railTopPx, bottom: BODY_TOP_PADDING }}
        className={[
          embedded
            // Embedded: flow layout — body sits directly under the inline
            // header above. No max-height, no absolute positioning; the
            // parent page owns the scroll context.
            ? 'relative flex flex-col'
            : 'fixed left-0 z-0 flex flex-col',
          !embedded && bleed
            ? 'right-0 px-4 sm:px-6 lg:px-8'
            : !embedded ? 'right-0' : '',
          // `max-w-6xl` for narrow mode — stay centered horizontally when
          // not full-bleed. Achieved with symmetric auto-margins on the
          // inner card rather than the outer container since the outer is
          // fixed-positioned and a max-width there wouldn't center. We only
          // reserve room on the right when the panel will actually render
          // (open AND not suppressed by the no-comments work-mode rule);
          // otherwise the inset would push the body off-center.
          !embedded && commentPanelOpen && railAvailable
            ? 'lg:right-[27rem]'
            : '',
        ].filter(Boolean).join(' ')}
      >
        <div
          className={[
            embedded
              // Embedded: drop min-h-0/flex-1 (no vertical filling) and
              // overflow-hidden (body should size to content, not clip).
              // Also skip the top border/rounding since the header above
              // already provides them — avoids a double border.
              ? 'flex flex-col rounded-b-lg border-x border-b border-gray-200 bg-white shadow-sm'
              : 'flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm',
            !embedded && !bleed ? 'mx-auto w-full max-w-6xl' : '',
          ].filter(Boolean).join(' ')}
        >
          <div
            className={
              embedded
                // Embedded: let the page scroll; bound horizontal overflow
                // so a wide module (code editors, tables) gets its own
                // horizontal scrollbar instead of forcing the page to scroll.
                ? 'min-w-0 max-w-full overflow-x-auto px-5 py-5'
                : 'min-h-0 flex-1 overflow-y-auto px-5 py-5'
            }
          >
            <module.View
              host={taskTypeHost}
              mode={bodyMode}
              viewerMode={mode}
              payload={parsedPayload}
              result={parsedResult}
              onChange={onChangeBody}
              facts={taskFacts}
              isAdminViewer={isAdminViewer}
            />
          </div>

          {error && (
            <div className="border-t border-red-100 bg-red-50 px-5 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <TaskShellFooter
            mode={mode}
            canEditResult={canEditResult}
            canClaimReview={canClaimReview}
            canRequestChanges={canRequestChangesNow}
            submitGate={submitGate}
            fieldLabels={fieldLabels}
            onFocusBlockedField={focusBlockedField}
            // Module-level draft validation — null means submittable, a string
            // is the user-facing reason (e.g. "Add a title for the challenge").
            // Only meaningful in work mode but cheap to compute always.
            submitValidation={blockingValidationIssues[0]?.message ?? null}
            busy={busy}
            pendingAction={pendingAction}
            actions={footerActions}
            validatorEnabled={validatorEnabled && !!module.hasLlmValidator}
            validatorStatus={validatorStatus}
            validatorCount={validatorVisibleCount}
            validatorError={validatorError}
            workFooter={WorkFooter ? (
              <WorkFooter
                result={parsedResult}
                onChange={onResultChange}
                onSubmit={footerActions?.onSubmit}
                busy={busy}
                pendingAction={pendingAction}
                submitGate={submitGate}
                submitValidation={blockingValidationIssues[0]?.message ?? null}
              />
            ) : null}
          />
        </div>
      </div>
        </div>{/* /left column (header + body) */}

      {/* Comment rail. Parked on the right as a fixed sibling — NOT a drawer.
          Body gets matching right-padding so content isn't obscured. The top
          offset is measured (app header + task header) so the rail starts
          flush with the scrolling body section. Panel scrolls independently.
          Embedded mode renders the rail as a plain inline card below the task
          card instead — fixed positioning would break out of the host page. */}
      {commentPanelOpen && activePanel && (
        <aside
          aria-label={activePanel.title}
          // Panel sizes to its content. `max-h` is the safety cap so a long
          // comment list scrolls internally instead of spilling past the
          // viewport. Without `bottom`, the panel ends right after its last
          // comment rather than stretching empty space to the viewport floor.
          style={
            embedded
              ? undefined
              // `height` (not just `maxHeight`) gives the inner flex chain a
              // definite parent height to size against. With only maxHeight
              // some browsers compute the aside's height as `auto`, and the
              // nested `flex-1 overflow-y-auto` in CommentColumn never gets
              // a bounded basis to scroll within. Belt + suspenders: keep
              // the same value as the maxHeight cap so visual behavior is
              // identical, just deterministic.
              : {
                  top: railTopPx,
                  height: `calc(100vh - ${railTopPx + BODY_TOP_PADDING}px)`,
                  maxHeight: `calc(100vh - ${railTopPx + BODY_TOP_PADDING}px)`,
                }
          }
          className={
            embedded
              // Embedded: full-width below the body on small screens; on lg+
              // it becomes a fixed-width right rail next to the body via the
              // wrapping flex row above. `lg:self-start` keeps the aside
              // hugging the top of the row instead of stretching to match the
              // body card's height.
              ? 'relative flex w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm lg:w-96 lg:shrink-0 lg:self-start'
              : 'fixed right-4 z-10 hidden w-96 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm sm:right-6 lg:right-8 lg:flex'
          }
        >
          {/* Right padding is intentionally tighter than left (`pr-3` vs
              `pl-4`) so the X button's visual center lines up with the right
              edge of the card rows inside the panel body (which sit in a
              `p-3` wrapper). Keeps the title's left padding comfortable
              while pulling the close affordance outward. */}
          <div className="flex items-center justify-between border-b border-gray-200 pl-4 pr-3 py-3">
            <div role="tablist" aria-label="Review panel" className="flex min-w-0 items-center gap-3">
              {panels.map((panel) => {
                const badge = panel.badgeCount(panelContext);
                const selected = activePanel.id === panel.id;
                const { Icon } = panel;
                return (
                  <button
                    key={panel.id}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    onClick={() => setPreferredPanelId(panel.id)}
                    className={[
                      'inline-flex items-center gap-1.5 text-sm font-semibold',
                      selected ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700',
                    ].join(' ')}
                  >
                    {Icon && <Icon className="h-3.5 w-3.5" />}
                    {panel.label}
                    {badge > 0 && (
                      <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
                        {badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setCommentPanelOpen(false)}
              aria-label={`Close ${activePanel.title.toLowerCase()}`}
              className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div
            className={
              embedded
                // Embedded: grow to content (no flex-1 inside a non-flex
                // parent) and keep the comment list's own scroll.
                ? 'max-h-[60vh] overflow-y-auto'
                : 'min-h-0 flex-1 overflow-hidden'
            }
          >
            <activePanel.Component context={panelContext} />
          </div>
        </aside>
      )}
      </div>{/* /layout shell */}

      <FieldCommentDialog
        open={commentComposer !== null}
        fieldPath={commentComposer?.fieldPath ?? null}
        busy={pendingAction === 'addFieldComment'}
        onClose={() => setCommentComposer(null)}
        onSubmit={async (text) => {
          if (!actions?.onAddFieldComment || !commentComposer) return;
          await actions.onAddFieldComment(commentComposer.fieldPath, text, commentComposer.fieldAnchor);
          setCommentComposer(null);
        }}
      />

      {submitConfirmOpen && submitConfirmation && (
        <submitConfirmation.Component
          result={parsedResult}
          onChange={onResultChange}
          busy={pendingAction === 'submit'}
          onCancel={() => setSubmitConfirmOpen(false)}
          onConfirm={async () => {
            if (!actions?.onSubmit) return;
            try {
              await actions.onSubmit();
            } finally {
              // Close either way: on success the page navigates away, and on
              // failure the shell's error strip needs to be visible.
              setSubmitConfirmOpen(false);
            }
          }}
        />
      )}

      <ConfirmDialog
        open={abandonOpen}
        title="Release this task?"
        message={
          'This returns the task to the available pool for any other expert to claim.\n\n'
          + 'Your draft will be discarded — whoever picks it up next starts from a blank slate. '
          + 'The task itself stays in the project; only your work on it goes away.'
        }
        confirmLabel="Release task"
        tone="danger"
        busy={pendingAction === 'abandon'}
        onCancel={() => setAbandonOpen(false)}
        onConfirm={async () => {
          if (!actions?.onAbandon) return;
          await actions.onAbandon();
          setAbandonOpen(false);
        }}
      />

      {taskExampleOpen && (
        <TaskExampleModal
          module={module}
          onClose={() => setTaskExampleOpen(false)}
        >
          <TaskExampleBody module={module} instructions={instructions} />
        </TaskExampleModal>
      )}

      <ConfirmDialog
        open={releaseReviewOpen}
        title="Release this review?"
        message={
          'This drops your claim on the review and puts the task back in the '
          + 'awaiting-review pool for another reviewer to pick up.\n\n'
          + "The expert's submission stays as-is; any comments you've left are "
          + 'preserved. You can claim it again later if no one else has.'
        }
        confirmLabel="Release review"
        busy={pendingAction === 'releaseReview'}
        onCancel={() => setReleaseReviewOpen(false)}
        onConfirm={async () => {
          if (!actions?.onReleaseReview) return;
          await actions.onReleaseReview();
          setReleaseReviewOpen(false);
        }}
      />
    </ReviewProvider>
  );
}

/** The modal receives this body as a neutral ReactNode boundary. */
function TaskExampleBody({
  module,
  instructions,
}: {
  module: TaskTypeModule<any, any>;
  instructions?: TaskTypeInstructions | null;
}) {
  const [result, setResult] = useState<unknown>(module.sampleResult ?? null);
  return (
    <TaskShell
      mode="preview"
      module={module}
      result={result}
      onResultChange={setResult}
      instructions={instructions}
      embedded
    />
  );
}
